import { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useMessagingResource } from '../resource/use-messaging-resource';
import { RecipientSearch } from './recipient-search';
import { ConversationRecipientSearchResultView } from '@mark43/cad-api';

export type State =
    | {
          isError: false;
          isFetching: false;
      }
    | {
          isError: false;
          isFetching: true;
      }
    | {
          isError: true;
          isFetching: false;
      };

export type Actions = { type: 'SET_FETCHING' } | { type: 'SET_FETCHED' } | { type: 'SET_ERROR' };

export const stateReducer = (state: State, action: Actions): State => {
    switch (action.type) {
        case 'SET_FETCHING':
            return {
                isError: false,
                isFetching: true,
            };
        case 'SET_FETCHED':
            return {
                isError: false,
                isFetching: false,
            };
        case 'SET_ERROR':
            return {
                isError: true,
                isFetching: false,
            };
        default:
            return state;
    }
};

export const RecipientsQuickSearchContext = createContext<
    | {
          isFetchingRecipientLists: boolean;
          isErrorFetchingRecipientLists: boolean;
          queryRecipients: (
              searchText: string
          ) => ConversationRecipientSearchResultView | undefined;
          refetchRecipientLists: () => Promise<void>;
      }
    | undefined
>(undefined);

type RecipientSearchesByEntityType = {
    callTakerStationsSearch: RecipientSearch;
    dispatchAreasSearch: RecipientSearch;
    dispatchAreaRespondersSearch: RecipientSearch;
    allOnlineSearch: RecipientSearch;
    messagingGroupsSearch: RecipientSearch;
    unitsSearch: RecipientSearch;
    usersSearch: RecipientSearch;
};

export const RecipientsQuickSearchProvider: React.FC = ({ children }) => {
    const [state, dispatch] = useReducer(stateReducer, {
        isError: false,
        isFetching: true,
    });

    const recipientSearchesByEntityType = useRef<RecipientSearchesByEntityType>();

    const initRecipientSearches = useMemo(
        () => ({
            callTakerStations,
            dispatchAreas,
            dispatchAreaResponders,
            allOnline,
            messagingGroups,
            units,
            users,
        }: ConversationRecipientSearchResultView) => {
            recipientSearchesByEntityType.current = {
                callTakerStationsSearch: new RecipientSearch(callTakerStations),
                dispatchAreasSearch: new RecipientSearch(dispatchAreas),
                dispatchAreaRespondersSearch: new RecipientSearch(dispatchAreaResponders),
                allOnlineSearch: new RecipientSearch(allOnline),
                messagingGroupsSearch: new RecipientSearch(messagingGroups),
                unitsSearch: new RecipientSearch(units),
                usersSearch: new RecipientSearch(users),
            };
        },
        []
    );

    const { getRecipientSearchItems } = useMessagingResource();

    const fetchRecipientLists = useCallback(
        async () => {
            if (!getRecipientSearchItems) {
                return;
            }
            try {
                dispatch({ type: 'SET_FETCHING' });
                const recipientSearchItems = await getRecipientSearchItems();
                if (recipientSearchItems) {
                    initRecipientSearches(recipientSearchItems);
                }
                dispatch({ type: 'SET_FETCHED' });
            } catch (error) {
                dispatch({ type: 'SET_ERROR' });
            }
        },
        // we don't want to re-render when onError ref changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [getRecipientSearchItems, initRecipientSearches]
    );

    useEffect(() => {
        fetchRecipientLists();
    }, [fetchRecipientLists]);

    const queryRecipients = (searchText: string) => {
        if (recipientSearchesByEntityType.current === undefined) {
            return;
        }
        const {
            callTakerStationsSearch,
            dispatchAreasSearch,
            dispatchAreaRespondersSearch,
            allOnlineSearch,
            messagingGroupsSearch,
            unitsSearch,
            usersSearch,
        } = recipientSearchesByEntityType.current;
        const searchResult = {
            callTakerStations: callTakerStationsSearch.search(searchText),
            dispatchAreas: dispatchAreasSearch.search(searchText),
            dispatchAreaResponders: dispatchAreaRespondersSearch.search(searchText),
            allOnline: allOnlineSearch.search(searchText),
            messagingGroups: messagingGroupsSearch.search(searchText),
            units: unitsSearch.search(searchText),
            users: usersSearch.search(searchText),
        };
        return searchResult;
    };

    return (
        <RecipientsQuickSearchContext.Provider
            value={{
                isFetchingRecipientLists: state.isFetching,
                isErrorFetchingRecipientLists: state.isError,
                queryRecipients,
                refetchRecipientLists: fetchRecipientLists,
            }}
        >
            {children}
        </RecipientsQuickSearchContext.Provider>
    );
};
